import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { ProcessTimeline, SERVICES_PROCESS_STEPS, TrustBadgesDark } from "@/components/PageBlocks";
import { ShieldCheck, TrendingUp, HeartHandshake, UserCog, ArrowRight, ArrowUpRight } from "lucide-react";
import { PRIMARY_SERVICES, EXTRA_SERVICES } from "@/lib/services";
import { ServiceEnquiryDialog } from "@/components/ServiceEnquiry";

const HERO_IMG = "/services-hero.png";
const CTA_IMG  = "/services-cta.png";

const HERO_PILLARS = [
    { icon: ShieldCheck,   t: "Legal First",   d: "Every property is 100% verified." },
    { icon: TrendingUp,    t: "Transparent",   d: "No hidden charges, no surprises." },
    { icon: HeartHandshake,t: "Reliable",      d: "Expert guidance you can trust." },
    { icon: UserCog,       t: "Personalised",  d: "Solutions tailored to your needs." },
];


export default function Services() {
    const [enquire, setEnquire] = useState(null);
    return (
        <PageShell navVariant="dark">
            {/* ================= HERO ================= */}
            <section data-testid="services-hero" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/70 to-transparent"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-20 md:pt-44 md:pb-24">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase mb-5">Our Services</div>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-[76px] leading-[1.02] max-w-4xl">
                        End-to-End Services.<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Built Around Your Trust.</em>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 leading-relaxed text-base md:text-lg">
                        From property discovery to possession and beyond, EBG Realty is with you at every step
                        of your real estate journey.
                    </p>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {HERO_PILLARS.map((p, i) => (
                            <div key={i} data-testid={`hero-pillar-${i}`} className="text-center md:text-left">
                                <span className="w-12 h-12 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center mx-auto md:mx-0">
                                    <p.icon size={18} strokeWidth={1.8}/>
                                </span>
                                <div className="font-serif text-lg md:text-xl mt-3">{p.t}</div>
                                <p className="text-white/60 text-[13px] mt-1 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= SERVICE CARDS ================= */}
            <section data-testid="services-grid" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Our Full Suite</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">
                            Comprehensive<br/>Real Estate Solutions
                        </h2>
                        <p className="text-black/60 mt-5 leading-relaxed">
                            We simplify real estate decisions by combining legal verification, market intelligence
                            and personalised advisory — so you invest with confidence.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                        {PRIMARY_SERVICES.map((s) => <ServiceCard key={s.slug} s={s} onEnquire={() => setEnquire(s.t)}/>)}
                    </div>

                    <div className="mt-16" data-testid="services-more">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mb-5">More Services</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
                            {EXTRA_SERVICES.map((s) => (
                                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-more-${s.slug}`} className="group bg-white p-5 flex items-start gap-4 hover:bg-[var(--ebg-gold)]/[0.06] transition-colors">
                                    <span className="w-10 h-10 rounded-full bg-[var(--ebg-gold)]/15 text-[var(--ebg-gold)] flex items-center justify-center shrink-0"><s.icon size={16}/></span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-serif text-lg leading-tight">{s.t}</div>
                                        <p className="text-black/55 text-[12.5px] mt-1 leading-relaxed">{s.d}</p>
                                    </div>
                                    <ArrowUpRight size={15} className="text-black/30 group-hover:text-[var(--ebg-gold)] shrink-0"/>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            <ServiceEnquiryDialog service={enquire} open={!!enquire} onOpenChange={(o) => !o && setEnquire(null)}/>

            {/* ================= 6-STEP PROCESS ================= */}
            <ProcessTimeline
                variant="dark"
                eyebrow="Our Process"
                heading="A Simple. Transparent. Secure Process."
                steps={SERVICES_PROCESS_STEPS}
            />

            {/* ================= TRUST BADGES DARK ================= */}
            <TrustBadgesDark/>

            {/* ================= CTA ================= */}
            <section data-testid="services-cta" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={CTA_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18]/95 via-[#0a0d18]/80 to-[#0a0d18]/60"/>
                <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Get Started</div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.03]">
                        Ready to Make the<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Right Move?</em>
                    </h2>
                    <p className="text-white/70 max-w-lg mx-auto mt-6 leading-relaxed">
                        Connect with our experts and let us help you find, verify & secure the right property.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="services-cta-btn"
                        className="mt-10 inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black px-10 py-5 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-white transition-colors"
                    >
                        Book a Free Consultation <ArrowRight size={13}/>
                    </Link>
                </div>
            </section>
        </PageShell>
    );
}

function ServiceCard({ s, onEnquire }) {
    return (
        <article data-testid={`service-card-${s.slug}`} className="group bg-white border border-black/8 hover:shadow-[0_25px_50px_-25px_rgba(0,0,0,0.25)] transition-shadow duration-500 flex flex-col">
            <div className="relative">
                <div className="relative aspect-[16/10] overflow-hidden bg-black/5">
                    <img src={s.img} alt={s.t} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"/>
                </div>
                <div className="absolute -bottom-6 left-5 w-12 h-12 rounded-full bg-[var(--ebg-gold)] border-4 border-white shadow-lg flex items-center justify-center text-black z-10">
                    <s.icon size={19} strokeWidth={1.8}/>
                </div>
            </div>
            <div className="p-5 pt-9 flex flex-col flex-1">
                <h3 className="font-serif text-xl leading-tight">{s.t}</h3>
                <p className="text-black/60 text-[13.5px] mt-2.5 leading-relaxed flex-1">{s.d}</p>
                <div className="mt-5 grid grid-cols-2 gap-2">
                    <Link to={`/services/${s.slug}`} data-testid={`service-explore-${s.slug}`} className="border border-black/15 py-2.5 text-[10px] uppercase tracking-[0.18em] font-bold text-center hover:border-black transition-colors inline-flex items-center justify-center gap-1">
                        Learn More <ArrowRight size={11}/>
                    </Link>
                    <button onClick={onEnquire} data-testid={`service-enquire-${s.slug}`} className="bg-black text-white py-2.5 text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors">
                        Enquire Now
                    </button>
                </div>
            </div>
        </article>
    );
}
